import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tooltip,
  useTheme,
  IconButton,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CloseIcon from "@mui/icons-material/Close";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PercentIcon from "@mui/icons-material/Percent";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import RedeemIcon from "@mui/icons-material/Redeem";
import styles from "./BonusTopBar.module.scss";
import { bonusApi } from "../bonusApi";
import { targetsApi } from "../targetsApi";
import { bountyApi } from "../bountyApi";
import { commissionsApi } from "../commissionsApi";
import { useBonusBar } from "../../context/BonusBarContext";

const BONUS_TOP_BAR_ROLES = [
  "admin",
  "Manager",
  "Counsellor",
  "Visa Counsellor",
  "Visa&Application Counsellor",
];

const CYCLE_INTERVAL_MS = 12000;

function getTimeLeft(endDateStr) {
  if (!endDateStr) return "";
  const end = new Date(endDateStr.slice(0, 10));
  end.setHours(23, 59, 59, 999);
  const now = new Date();
  const ms = end - now;
  if (ms <= 0) return "Ended";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days >= 1) return `${days} day${days !== 1 ? "s" : ""} left`;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours >= 1) return `${hours}h left`;
  return "Ending soon";
}

/** Human-readable reward from bonus (real reward) */
function getRewardLabel(b) {
  if (!b) return "Reward";
  const desc = (b.reward_description || "").trim();
  const type = (b.reward_type || "").toLowerCase();
  if (type === "cash" && desc) {
    const match = desc.replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
    const amount = match ? match[1] : null;
    if (amount) return `LKR ${Number(amount).toLocaleString()}`;
  }
  return desc || b.reward_type || "Reward";
}

/** Conversion rate: "X% above target" / "X% below target" */
function getRateTargetStatus(current, goal) {
  if (goal == null || goal === 0) return null;
  const diff = current - goal;
  if (diff > 0) return `${diff.toFixed(1)}% above target`;
  if (diff < 0) return `${Math.abs(diff).toFixed(1)}% below target`;
  return "On target";
}

function BonusTopBar({ role }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { bonusBarDismissed: dismissed, dismissBonusBanner } = useBonusBar();
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [, setTick] = useState(0);
  const [activeTab, setActiveTab] = useState("bonus");
  const [myTargets, setMyTargets] = useState([]);
  const [targetIndex, setTargetIndex] = useState(0);
  const [activeBounties, setActiveBounties] = useState([]);
  const [bountyIndex, setBountyIndex] = useState(0);
  const [myCommissions, setMyCommissions] = useState([]);

  useEffect(() => {
    if (!BONUS_TOP_BAR_ROLES.includes(role)) return;
    setLoading(true);
    bonusApi
      .active()
      .then((data) => {
        setBonuses(data.bonuses || []);
        setCurrentIndex(0);
      })
      .catch(() => setBonuses([]))
      .finally(() => setLoading(false));
  }, [role]);

  useEffect(() => {
    if (!BONUS_TOP_BAR_ROLES.includes(role)) return;
    targetsApi
      .my()
      .then((data) => {
        setMyTargets(data.targets || []);
        setTargetIndex(0);
      })
      .catch(() => {
        setMyTargets([]);
        setTargetIndex(0);
      });
  }, [role]);

  useEffect(() => {
    if (!BONUS_TOP_BAR_ROLES.includes(role)) return;
    bountyApi
      .active()
      .then((data) => setActiveBounties(data.bounties || []))
      .catch(() => setActiveBounties([]));
  }, [role]);

  useEffect(() => {
    if (!BONUS_TOP_BAR_ROLES.includes(role)) return;
    commissionsApi.my().then((data) => setMyCommissions(data.structures || [])).catch(() => setMyCommissions([]));
  }, [role]);

  useEffect(() => {
    if (activeBounties.length <= 1) return;
    const id = setInterval(() => setBountyIndex((i) => (i + 1) % activeBounties.length), CYCLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activeBounties.length]);

  useEffect(() => {
    if (bonuses.length <= 1) return;
    const id = setInterval(() => setCurrentIndex((i) => (i + 1) % bonuses.length), CYCLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [bonuses.length]);

  useEffect(() => {
    if (myTargets.length <= 1) return;
    const id = setInterval(() => setTargetIndex((i) => (i + 1) % myTargets.length), CYCLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [myTargets.length]);

  useEffect(() => {
    if (myTargets.length === 0) return;
    setTargetIndex((i) => (i >= myTargets.length ? 0 : i));
  }, [myTargets.length]);

  useEffect(() => {
    if (bonuses.length === 0) return;
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, [bonuses.length]);

  // Reserve space in main content when bar is visible (must run unconditionally for hooks rules)
  useEffect(() => {
    if (!dismissed) {
      document.body.classList.add("bonus-bar-visible");
      return () => document.body.classList.remove("bonus-bar-visible");
    }
    document.body.classList.remove("bonus-bar-visible");
  }, [dismissed]);

  const hasBonuses = BONUS_TOP_BAR_ROLES.includes(role) && bonuses.length > 0 && !loading;
  const b = hasBonuses ? bonuses[currentIndex] : null;
  const showWidget = BONUS_TOP_BAR_ROLES.includes(role);

  // 2x2 widget: each button sets activeTab and bar content (Bonus / Commission=coming soon / Targets / Bounty=coming soon)
  const quickLinks = [
    { id: "bonus", label: "Bonus", icon: <EmojiEventsIcon sx={{ fontSize: 22 }} />, btnClass: "quickBtnBonus" },
    { id: "commission", label: "Commission", icon: <PercentIcon sx={{ fontSize: 22 }} />, btnClass: "quickBtnCommission" },
    { id: "targets", label: "Targets", icon: <TrackChangesIcon sx={{ fontSize: 22 }} />, btnClass: "quickBtnTargets" },
    { id: "bounty", label: "Bounty", icon: <RedeemIcon sx={{ fontSize: 22 }} />, btnClass: "quickBtnBounty" },
  ];

  if (!showWidget) return null;

  const isConversionRate = b?.metric === "conversion_rate";
  const goal = isConversionRate ? Math.min(100, Number(b?.goal) || 100) : Number(b?.goal) || 0;
  const current = isConversionRate
    ? Math.min(100, Number(b?.current_progress) || 0)
    : Number(b?.current_progress) ?? 0;
  // For conversion rate: bar is 0–100%, fill = current rate. For others: bar = % of goal.
  const barPct = isConversionRate ? current : (goal ? Math.min(100, (current / goal) * 100) : 0);
  const isCompleted = isConversionRate ? current >= goal : (goal && current >= goal) || barPct >= 100;
  const timeLeft = getTimeLeft(b?.end_date);
  const rewardLabel = getRewardLabel(b);
  const rateTargetStatus = isConversionRate ? getRateTargetStatus(current, goal) : null;

  const CARD_HEIGHT = 80;
  const COMMISSION_SOURCE_LABELS = {
    total_business_revenue: "revenue",
    total_conversions: "conversions",
    conversions_by_program: "conversions",
    conversions_by_course: "conversions",
  };

  // Bar content by active tab (noCard = true when rendered inside the shared card with 2x2)
  const renderBarContent = (noCard = false) => {
    const cardClass = noCard ? styles.cardInner : styles.card;
    if (activeTab === "commission") {
      const s = myCommissions[0];
      const progress = s?.progress || {};
      const rules = progress.rules || [];
      const rule = rules.find((r) => r.next_bracket) || rules[0];
      const nextBracket = rule?.next_bracket;
      const metricVal = Number(rule?.metric_value) ?? 0;
      const nextMin = nextBracket?.min_value != null ? Number(nextBracket.min_value) : null;
      const needed = nextMin != null && nextMin > metricVal ? Math.ceil(nextMin - metricVal) : 0;
      const unit = rule ? (COMMISSION_SOURCE_LABELS[rule.source] || "units") : "units";
      const nextLabel =
        nextBracket && rule
          ? rule.rule_type === "percentage"
            ? `Next: ${Number(nextBracket.value).toFixed(0)}%`
            : `Next: $${Number(nextBracket.value).toFixed(0)} per ${unit === "conversions" ? "conversion" : "unit"}`
          : null;
      const hasStructures = myCommissions.length > 0;

      return (
        <Box
          className={cardClass}
          onClick={() => navigate("/my-commissions")}
          sx={{ height: CARD_HEIGHT, minHeight: CARD_HEIGHT, cursor: "pointer" }}
        >
          <Box className={styles.left}>
            <Box className={styles.iconWrap}>
              <PercentIcon className={styles.trophyIcon} />
            </Box>
            <Box className={styles.titleBlock}>
              <Typography className={styles.title} variant="subtitle2">
                {s?.name || "Commissions"}
              </Typography>
              <Typography className={styles.count} variant="h6" component="span">
                {hasStructures
                  ? nextLabel
                    ? needed > 0
                      ? `${needed} more ${unit} to next bracket`
                      : "At top bracket"
                    : "View your commission structures"
                  : "No commission structures assigned"}
                <Typography component="span" className={styles.countUnit}>
                  {nextLabel ? ` · ${nextLabel}` : ""}
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Box className={styles.right}>
            {nextLabel && needed > 0 && (
              <Typography className={styles.statValue}>{needed}</Typography>
            )}
          </Box>
        </Box>
      );
    }
    if (activeTab === "bounty") {
      const count = activeBounties.length;
      const bounty = count > 0 ? activeBounties[bountyIndex] : null;
      const convCount = bounty?.current_user_conversion_count ?? 0;
      const required = bounty?.conversions_required ?? 1;
      const bountyPct = required > 0 ? Math.min(100, (convCount / required) * 100) : 0;
      const rewardLabel = bounty
        ? `${bounty.reward_type || "Cash"}: ${Number(bounty.reward_value ?? 0).toLocaleString()}`
        : null;

      if (count === 0) {
        return (
          <Box
            className={cardClass}
            sx={{
              height: CARD_HEIGHT,
              minHeight: CARD_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 2,
            }}
          >
            <Typography className={styles.comingSoonText}>No available bounties</Typography>
          </Box>
        );
      }
      return (
        <Box
          className={cardClass}
          onClick={() => navigate("/bounties")}
          sx={{ height: CARD_HEIGHT, minHeight: CARD_HEIGHT, cursor: "pointer" }}
        >
          <Box className={styles.left}>
            <Box className={styles.iconWrap}>
              <RedeemIcon className={styles.trophyIcon} />
            </Box>
            <Box className={styles.titleBlock}>
              <Typography className={styles.title} variant="subtitle2">
                Bounties
              </Typography>
                <Typography className={styles.count} variant="h6" component="span">
                {bounty?.name || `${count} available`}
                <Typography component="span" className={styles.countUnit}>
                  {" "}
                  · {bounty?.scope_display ?? "Overall"}
                  {" "}
                  · {convCount} / {required} conversions
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Box className={styles.center}>
            <Box className={styles.progressContainer}>
              <div
                className={`${styles.progressTrack} ${bountyPct >= 100 ? styles.progressComplete : ""}`}
                role="progressbar"
                aria-valuenow={Math.round(bountyPct)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Bounty progress"
              >
                <div className={styles.progressFill} style={{ width: `${bountyPct}%` }} />
              </div>
            </Box>
          </Box>
          <Box className={styles.right}>
            <Typography className={styles.statValue}>
              {Math.round(bountyPct)}%
            </Typography>
            {rewardLabel && (
              <Tooltip title={rewardLabel} arrow>
                <Box className={styles.rewardChip}>
                  <CardGiftcardIcon sx={{ fontSize: 14 }} />
                  <span className={styles.rewardLabel}>{rewardLabel}</span>
                </Box>
              </Tooltip>
            )}
            {count > 1 && (
              <Box className={styles.dots} onClick={(e) => e.stopPropagation()} sx={{ display: "flex", gap: 0.5 }}>
                {activeBounties.map((_, i) => (
                  <Box
                    key={i}
                    component="button"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setBountyIndex(i); }}
                    className={i === bountyIndex ? styles.dotActive : styles.dot}
                    aria-label={`Bounty ${i + 1} of ${count}`}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      );
    }
    if (activeTab === "targets") {
      if (myTargets.length === 0) {
        return (
          <Box
            className={cardClass}
            sx={{
              height: CARD_HEIGHT,
              minHeight: CARD_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 2,
            }}
          >
            <Typography className={styles.comingSoonText}>No targets assigned</Typography>
          </Box>
        );
      }
      const targetCount = myTargets.length;
      const t = myTargets[targetIndex] ?? myTargets[0];
      const tPct = t.goal ? Math.min(100, (Number(t.current_progress) / Number(t.goal)) * 100) : 0;
      return (
        <Box
          className={cardClass}
          onClick={() => t?.id && navigate(`/targets/${t.id}`)}
          sx={{ height: CARD_HEIGHT, minHeight: CARD_HEIGHT, cursor: "pointer" }}
        >
          <Box className={styles.left}>
            <Box className={styles.iconWrap}>
              <TrackChangesIcon className={styles.trophyIcon} />
            </Box>
            <Box className={styles.titleBlock}>
              <Typography className={styles.title} variant="subtitle2">
                Your Targets
              </Typography>
              <Typography className={styles.count} variant="h6" component="span">
                {t.name}
                <Typography component="span" className={styles.countUnit}>
                  {" "}
                  · {t.scope_display ?? "Overall"}
                  {" "}
                  · {Number(t.current_progress).toLocaleString()} / {Number(t.goal).toLocaleString()} {t.metric_label}
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Box className={styles.center}>
            <Box className={styles.progressContainer}>
              <div
                className={styles.progressTrack}
                role="progressbar"
                aria-valuenow={Math.round(tPct)}
                aria-label="Target progress"
              >
                <div className={styles.progressFill} style={{ width: `${tPct}%` }} />
              </div>
            </Box>
          </Box>
          <Box className={styles.right}>
            <Typography className={styles.statValue}>{Math.round(tPct)}%</Typography>
            {targetCount > 1 && (
              <Box className={styles.dots} onClick={(e) => e.stopPropagation()} sx={{ display: "flex", gap: 0.5 }}>
                {myTargets.map((_, i) => (
                  <Box
                    key={myTargets[i].id ?? i}
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTargetIndex(i);
                    }}
                    className={i === targetIndex ? styles.dotActive : styles.dot}
                    aria-label={`Target ${i + 1} of ${targetCount}`}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      );
    }
    // activeTab === "bonus" — show bonus progress
    if (!hasBonuses) {
      return (
        <Box
          className={cardClass}
          sx={{
            height: CARD_HEIGHT,
            minHeight: CARD_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
          }}
        >
          <Typography className={styles.comingSoonText}>No active bonuses</Typography>
        </Box>
      );
    }
    return (
      <Box
        className={cardClass}
        onClick={() => b && navigate(`/bonuses/${b.id}`)}
        sx={{ height: CARD_HEIGHT, minHeight: CARD_HEIGHT, cursor: "pointer" }}
      >
        <Box className={styles.left}>
          <Box className={styles.iconWrap}>
            <EmojiEventsIcon className={styles.trophyIcon} />
          </Box>
          <Box className={styles.titleBlock}>
            <Typography className={styles.title} variant="subtitle2">
              Team Bonus Progress
            </Typography>
            {b?.scope_display != null && b.scope_display !== "" && (
              <Typography variant="caption" className={styles.countUnit} sx={{ display: "block", mt: 0.25 }}>
                Scope: {b.scope_display}
              </Typography>
            )}
            <Typography className={styles.count} variant="h6" component="span">
              {isConversionRate
                ? `${Number(current).toFixed(1)}%`
                : `${Number(current).toLocaleString()} / ${Number(goal).toLocaleString()}`}
              <Typography component="span" className={styles.countUnit}>
                {isConversionRate ? " conversion rate" : " registrations"}
              </Typography>
            </Typography>
            {rateTargetStatus && (
              <Typography
                className={
                  rateTargetStatus.startsWith("On target")
                    ? styles.rateStatusOnTarget
                    : rateTargetStatus.includes("above")
                    ? styles.rateStatusAbove
                    : styles.rateStatusBelow
                }
                variant="caption"
              >
                {rateTargetStatus}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={styles.center}>
          <Tooltip
            title={rewardLabel ? `Next reward: ${rewardLabel} at ${isConversionRate && goal != null ? `${goal}%` : "100%"}` : "Bonus progress"}
            arrow
            placement="top"
          >
            <Box className={styles.progressContainer}>
              <div
                className={`${styles.progressTrack} ${isCompleted ? styles.progressComplete : ""}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(barPct)}
                aria-label={isConversionRate ? "Conversion rate 0–100%" : "Bonus progress"}
              >
                <div className={styles.progressFill} style={{ width: `${barPct}%` }} />
                {[25, 50, 75].map((pct) => (
                  <div key={pct} className={styles.progressMilestone} style={{ left: `${pct}%` }} aria-hidden />
                ))}
                {isConversionRate && goal != null && goal > 0 && goal < 100 && (
                  <div
                    className={styles.targetLine}
                    style={{ left: `${goal}%` }}
                    title={`Target: ${goal}%`}
                    aria-hidden
                  />
                )}
              </div>
            </Box>
          </Tooltip>
        </Box>
        <Box className={styles.right}>
          <Box className={styles.stat}>
            <Typography className={styles.statValue}>
              {isConversionRate ? `${Math.round(current)}%` : `${Math.round(barPct)}%`}
            </Typography>
            <Typography className={styles.statLabel}>
              {isConversionRate ? "Rate" : "Complete"}
            </Typography>
          </Box>
          <Tooltip title={rewardLabel} arrow>
            <Box className={styles.rewardChip}>
              <CardGiftcardIcon sx={{ fontSize: 14 }} />
              <span className={styles.rewardLabel}>{rewardLabel}</span>
            </Box>
          </Tooltip>
          {timeLeft && (
            <Typography className={styles.timeLeft}>{timeLeft}</Typography>
          )}
          {bonuses.length > 1 && (
            <Box
              className={styles.dots}
              onClick={(e) => e.stopPropagation()}
              sx={{ display: "flex", gap: 0.5 }}
            >
              {bonuses.map((_, i) => (
                <Box
                  key={i}
                  component="button"
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                  className={i === currentIndex ? styles.dotActive : styles.dot}
                  aria-label={`Bonus ${i + 1} of ${bonuses.length}`}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Dismissed: bar hidden; reopen from sidebar (green control next to theme / notifications).
  if (dismissed) {
    return null;
  }

  return (
    <Box
      className={styles.wrapper}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0,
        boxSizing: "border-box",
        [theme.breakpoints.down("md")]: {
          left: 16,
          width: "calc(100% - 32px)",
        },
      }}
      role="region"
      aria-label="Bonus and rewards"
    >
      <Box className={styles.card} sx={{ display: "flex", alignItems: "stretch", gap: 0, width: "100%", minWidth: 0, height: CARD_HEIGHT, minHeight: CARD_HEIGHT }}>
        <Box className={styles.barSection} sx={{ flex: 1, minWidth: 0 }}>
          {renderBarContent(true)}
        </Box>
        <Box
          className={styles.quickWidget}
          aria-label="Quick links: Bonus, Commission, Targets, Bounty"
        >
          <Box className={styles.quickGrid}>
            {quickLinks.map((item) => (
              <Tooltip key={item.id} title={item.label} arrow placement="bottom">
                <IconButton
                  className={`${styles.quickBtn} ${styles[item.btnClass]} ${activeTab === item.id ? styles.quickBtnActive : ""}`}
                  onClick={() => setActiveTab(item.id)}
                  aria-label={item.label}
                  aria-pressed={activeTab === item.id}
                  size="small"
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <IconButton
            size="small"
            className={styles.closeBtn}
            onClick={dismissBonusBanner}
            aria-label="Dismiss banner"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default BonusTopBar;
